import React, { useState, useEffect } from 'react'
import {
  FaPlus,
  FaCloud,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaImages,
  FaQuestionCircle,
  FaTimes,
  FaSun,
  FaCloudRain,
} from 'react-icons/fa'
import './FloatingActionButton.css'

const FloatingActionButton = () => {
  const [isOpen, setIsOpen] = useState(true)
  const [showWeather, setShowWeather] = useState(false)
  const [weatherData, setWeatherData] = useState({
    temperature: null,
    condition: 'Loading...',
    location: 'Ampthilldowns, Poondi',
    forecast: [],
  })
  const [weatherLoading, setWeatherLoading] = useState(false)
  const [weatherError, setWeatherError] = useState(null)

  // Kodaikanal/Poondi coordinates (Ampthilldowns location)
  const RESORT_COORDINATES = {
    lat: 10.1926, // Kodaikanal latitude
    lon: 77.4883, // Kodaikanal longitude
  }

  // Fetch real weather data from Open-Meteo API (free, no API key required)
  const fetchWeatherData = async () => {
    setWeatherLoading(true)
    setWeatherError(null)

    try {
      // Use Open-Meteo API (free, no API key required, accurate weather data)
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${RESORT_COORDINATES.lat}&longitude=${RESORT_COORDINATES.lon}&current=temperature_2m,weather_code,relative_humidity_2m,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min,weather_code&timezone=Asia%2FKolkata&forecast_days=3`
      )

      if (!response.ok) {
        throw new Error('Weather API request failed')
      }

      const data = await response.json()

      // Map weather codes to conditions (WMO Weather interpretation codes)
      const getWeatherCondition = (code) => {
        if (code === 0) return 'Clear sky'
        if (code <= 3) return 'Partly cloudy'
        if (code <= 49) return 'Foggy'
        if (code <= 59) return 'Drizzle'
        if (code <= 69) return 'Rain'
        if (code <= 79) return 'Snow'
        if (code <= 84) return 'Rain showers'
        if (code <= 86) return 'Snow showers'
        if (code <= 99) return 'Thunderstorm'
        return 'Unknown'
      }

      const current = data.current
      const daily = data.daily

      setWeatherData({
        temperature: Math.round(current.temperature_2m),
        condition: getWeatherCondition(current.weather_code),
        location: 'Ampthilldowns, Poondi',
        humidity: Math.round(current.relative_humidity_2m),
        windSpeed: Math.round(current.wind_speed_10m * 3.6), // Convert m/s to km/h
        forecast: [
          {
            day: 'Today',
            high: Math.round(daily.temperature_2m_max[0]),
            low: Math.round(daily.temperature_2m_min[0]),
          },
          {
            day: 'Tomorrow',
            high: Math.round(daily.temperature_2m_max[1]),
            low: Math.round(daily.temperature_2m_min[1]),
          },
          {
            day: new Date(daily.time[2]).toLocaleDateString('en-US', {
              weekday: 'short',
            }),
            high: Math.round(daily.temperature_2m_max[2]),
            low: Math.round(daily.temperature_2m_min[2]),
          },
        ],
        weatherCode: current.weather_code,
      })

      setWeatherLoading(false)
    } catch (error) {
      console.error('Error fetching weather data:', error)
      setWeatherError('Unable to load weather data. Please try again later.')
      setWeatherLoading(false)

      // Set fallback data
      setWeatherData({
        temperature: null,
        condition: 'Unable to load',
        location: 'Ampthilldowns, Poondi',
        forecast: [],
      })
    }
  }

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const handleAction = (action) => {
    switch (action) {
      case 'weather':
        if (!showWeather) {
          fetchWeatherData()
        }
        setShowWeather(!showWeather)
        break
      case 'book':
        // Navigate to stays page
        window.location.href = '/stays'
        break
      case 'directions':
        // Open Google Maps with resort location
        window.open(
          'https://maps.google.com/?q=Ampthill+Downs+The+Farm+Stay+39th+km+Kodai-Munnar+road+Poondi+Kodaikanal+Tamil+Nadu+624103',
          '_blank'
        )
        break
      case 'gallery':
        // Navigate to experiences page (gallery is part of experiences)
        window.location.href = '/gallery'
        break
      case 'enquiry':
        // Navigate to contact page
        window.location.href = '/contact'
        break
      default:
        break
    }
    setIsOpen(false)
  }

  return (
    <>
      {/* Weather Widget Overlay */}
      {showWeather && (
        <div className='weather-overlay'>
          <div className='weather-widget-container'>
            <button
              className='weather-close-btn'
              onClick={() => setShowWeather(false)}
            >
              <FaTimes />
            </button>
            <div className='weather-widget'>
              <h4>Current Weather</h4>
              {weatherLoading ? (
                <div className='weather-loading'>
                  <div className='loading-spinner'></div>
                  <p>Loading weather data...</p>
                </div>
              ) : weatherError ? (
                <div className='weather-loading'>
                  <p style={{ color: '#d32f2f' }}>{weatherError}</p>
                  <button
                    onClick={fetchWeatherData}
                    style={{
                      marginTop: '15px',
                      padding: '8px 16px',
                      background: 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div className='weather-info'>
                    <div className='weather-icon'>
                      {weatherData.condition?.toLowerCase().includes('rain') ||
                      weatherData.condition
                        ?.toLowerCase()
                        .includes('drizzle') ||
                      weatherData.condition
                        ?.toLowerCase()
                        .includes('shower') ? (
                        <FaCloudRain />
                      ) : weatherData.condition
                          ?.toLowerCase()
                          .includes('clear') ||
                        weatherData.condition
                          ?.toLowerCase()
                          .includes('sunny') ? (
                        <FaSun />
                      ) : (
                        <FaCloud />
                      )}
                    </div>
                    <div className='weather-details'>
                      <div className='temperature'>
                        {weatherData.temperature !== null
                          ? `${weatherData.temperature}°C`
                          : '--°C'}
                      </div>
                      <div className='condition'>
                        {weatherData.condition || 'Loading...'}
                      </div>
                      <div className='location'>{weatherData.location}</div>
                      {weatherData.humidity && (
                        <div
                          className='location'
                          style={{ fontSize: '0.85rem', marginTop: '5px' }}
                        >
                          Humidity: {weatherData.humidity}%
                        </div>
                      )}
                    </div>
                  </div>
                  {weatherData.forecast && weatherData.forecast.length > 0 && (
                    <div className='weather-forecast'>
                      {weatherData.forecast.map((item, index) => (
                        <div key={index} className='forecast-item'>
                          <span>{item.day}</span>
                          <span>
                            {item.high}°C / {item.low}°C
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <div className='floating-action-button'>
        {/* Menu Items */}
        <div className={`fab-menu ${isOpen ? 'fab-menu-open' : ''}`}>
          <div
            className='fab-menu-item'
            onClick={() => handleAction('weather')}
          >
            <FaCloud />
            <span>Weather</span>
          </div>
          <div className='fab-menu-item' onClick={() => handleAction('book')}>
            <FaCalendarCheck />
            <span>Book Stay</span>
          </div>
          <div
            className='fab-menu-item'
            onClick={() => handleAction('directions')}
          >
            <FaMapMarkerAlt />
            <span>Directions</span>
          </div>
          <div
            className='fab-menu-item'
            onClick={() => handleAction('gallery')}
          >
            <FaImages />
            <span>Gallery</span>
          </div>
          <div
            className='fab-menu-item'
            onClick={() => handleAction('enquiry')}
          >
            <FaQuestionCircle />
            <span>Quick Enquiry</span>
          </div>
        </div>

        {/* Main Button */}
        <button
          className={`fab-main ${isOpen ? 'fab-main-open' : ''}`}
          onClick={toggleMenu}
        >
          <FaPlus className={`fab-icon ${isOpen ? 'fab-icon-rotate' : ''}`} />
        </button>
      </div>
    </>
  )
}

export default FloatingActionButton
